import { Alarm, createAlarmModel } from '@akin-home/alarms';
import {
  Alarm as AlarmGraphql,
  CalendarItemType,
  CreateAlarmInput,
  UpdateAlarmInput as UpdateAlarmGraphqlInput,
} from '@akin-home/amplify';
import { createReminderItem } from '@akin-home/calendar-reminders';
import { createChecklistItem } from '@akin-home/checklists';
import { handleImageFieldsOnPost } from '@akin-home/images/frontend';
import {
  createRRule,
  getUntilDateString,
  serializeRRule,
} from '@akin-home/recurrence';
import { JSONStringifySafe } from '@akin-home/utils/json';
import { UpdateAlarmInput } from '../types';

export const alarmGraphqlToModel = (item: AlarmGraphql): Alarm =>
  createAlarmModel({
    id: item.id,
    habitatID: item.habitatID,
    title: item.title,
    emoji: item.emoji || '',
    start: item.start ? new Date(item.start) : new Date(),
    notes: item.notes || '',
    checklist: item.checklist
      ? JSON.parse(item.checklist).map(createChecklistItem)
      : [],
    participantIDs: item.participantIDS as string[] | undefined,
    recurrenceUntil: item.recurrenceUntil
      ? new Date(item.recurrenceUntil)
      : undefined,
    recurrence: item.recurrence && createRRule(item.recurrence),
    imageUrl: item.imageURL || undefined,
    image: item.image || undefined,
    imageCache: item.image || undefined,
    color: item.color || undefined,
    reminders: item.reminders
      ? JSON.parse(item.reminders).map(createReminderItem)
      : [],
  });

export const alarmModelToCreateGraphql = async (
  a: Alarm
): Promise<CreateAlarmInput> => {
  const alarm = await handleImageFieldsOnPost(a, {
    habitatID: a.habitatID,
  });

  return {
    type: CalendarItemType.Alarm,
    title: alarm.title,
    emoji: alarm.emoji,
    habitatID: alarm.habitatID,
    start: alarm.start.toISOString(),
    notes: alarm.notes,
    checklist: JSONStringifySafe(alarm.checklist || [], '[]'),
    participantIDS: alarm.participantIDs,
    recurrence: alarm.recurrence && serializeRRule(alarm.recurrence),
    recurrenceUntil: getUntilDateString(alarm.recurrence) || null,
    imageID: alarm.image?.id || null,
    imageURL: alarm.imageUrl || null,
    color: alarm.color || undefined,
    reminders: JSONStringifySafe(alarm.reminders || [], '[]'),
  };
};

export const alarmModelToUpdateGraphql = async (
  a: Alarm
): Promise<UpdateAlarmGraphqlInput> => ({
  ...(await alarmModelToCreateGraphql(a)),
  id: a.id,
});

export const alarmModelToUpdateData = (alarm: Alarm): UpdateAlarmInput => ({
  ...alarm,
  start: alarm.start.toISOString(),
  recurrence: alarm.recurrence && serializeRRule(alarm.recurrence),
  recurrenceUntil: getUntilDateString(alarm.recurrence) || null,
});

export const alarmDataToModel = (alarm: any): Alarm => ({
  ...alarm,
  start: new Date(alarm.start),
});
